import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../../helpers/axiosInstance";

const SCRIPT = "https://accounts.google.com/gsi/client";

/** Google's sign-in script, loaded once however many times it is asked for. */
let loading;
const loadGoogle = () => {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => {
        loading = null;
        reject(new Error("Could not load Google sign-in"));
      };
      document.head.appendChild(script);
    });
  }
  return loading;
};

/**
 * "Sign in with Google", Google's own button.
 *
 * Google only proves who the person is: it hands back an ID token, which goes
 * to the fund's server to be checked and matched to a member by email. The
 * server also says whether Google sign-in is on and which client to use, so
 * the button appears only when it is set up — nothing here to configure.
 *
 * `onCredential` is given the ID token; `onError` a message to show.
 */
function GoogleSignInButton({ onCredential, onError, disabled }) {
  const holder = useRef(null);
  const [clientId, setClientId] = useState(null);
  const callbacks = useRef({ onCredential, onError });
  callbacks.current = { onCredential, onError };

  useEffect(() => {
    let live = true;
    axiosInstance
      .get("/api/user/google/config")
      .then((response) => live && response.data?.enabled && setClientId(response.data.web_client_id))
      // No answer is no button: the password form still works.
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!clientId) return;
    let live = true;
    loadGoogle()
      .then(() => {
        if (!live || !holder.current) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => callbacks.current.onCredential(response.credential),
          ux_mode: "popup",
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        window.google.accounts.id.renderButton(holder.current, {
          type: "standard",
          theme: "filled_black",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
          logo_alignment: "left",
          width: Math.min(400, holder.current.offsetWidth || 320),
        });
      })
      .catch(() => live && callbacks.current.onError("Google sign-in could not load. Sign in with your password."));
    return () => {
      live = false;
    };
  }, [clientId]);

  if (!clientId) return null;
  return (
    <div className="signin-google">
      <div className="signin-google-or">
        <span>or</span>
      </div>
      <div ref={holder} className="signin-google-button" style={disabled ? { pointerEvents: "none", opacity: 0.5 } : undefined} />
    </div>
  );
}

export default GoogleSignInButton;
